import Badge from "@/common/components/Badge";
import Box from "@/common/components/Box";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters';
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CreateGovernmentAllocationDistributionValidationSchema, CreateGovernmentAllocationDistributionQuantityValidationSchema } from "@/features/privateCompanies/schemas/createGovernmentAllocationDistribution.schema";
import { CompanyGovernmentAllocationDistribution, CompanyGovernmentAllocationProductDistribution, ControlledByType, GovernmentAllocationAllowedCategoryAndProductData } from "@/features/privateCompanies/types/companyGovernmentAllocation.type";
import { FileBoxIcon } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { UnitTextField } from "@/common/components/UnitTextField";
import { unities, unitiesDecimals } from "@/common/constants/unities.constant";

interface ProductDistributionFormItemProps {
  productCategoryIndex: number;
  productIndex: number;
  productDistribution: CompanyGovernmentAllocationProductDistribution;
  allowedProduct: GovernmentAllocationAllowedCategoryAndProductData;
  onDelete: () => void;
  onTotalValueChange: () => void;
  distribution: CompanyGovernmentAllocationDistribution;
  productCategoryId: string;
  controlledBy?: ControlledByType;
}

const ProductDistributionFormItem = ({
  productCategoryIndex,
  productIndex,
  productDistribution,
  allowedProduct,
  onTotalValueChange,
  distribution,
  productCategoryId,
  controlledBy = 'currency',
}: ProductDistributionFormItemProps) => {
  const isQuantity = controlledBy === 'quantity';

  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<CreateGovernmentAllocationDistributionValidationSchema | CreateGovernmentAllocationDistributionQuantityValidationSchema>();

  const currentValue = useWatch({
    control,
    name: isQuantity
      ? `product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_quantity`
      : `product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_value`,
  })
  const allocated = Number(isQuantity ? allowedProduct?.allocated_quantity : allowedProduct?.allocated_total) || 0;
  const available = Number(isQuantity ? allowedProduct?.available_quantity : allowedProduct?.available_total) || 0;
  const distributed = Number(isQuantity ? allowedProduct?.distributed_quantity : allowedProduct?.distributed_total) || 0;
  const product = distribution?.product_categories_distributions
    ?.find((category) => category.product_category.id === productCategoryId)
    ?.products_distributions?.find((product) => product.product?.id === productDistribution?.product?.id);
  const availableForAllocation =
    Math.min(
      isQuantity ? unFormatUnknownUnit(currentValue || '0') : unFormatCurrency(currentValue || ''),
      Number(isQuantity ? product?.total_quantity : product?.total_value) || 0,
    ) + available;

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={FileBoxIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={productDistribution?.product?.display_name}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
      />

      <div className='flex flex-col gap-1'>
        {isQuantity ? (
          <UnitTextField
            label="Quantidade limite"
            unit={productDistribution.product.unit_for_quantity ? unities[productDistribution.product.unit_for_quantity] : undefined}
            decimals={productDistribution.product.unit_for_quantity ? unitiesDecimals[productDistribution.product.unit_for_quantity] : 2}
            autoCorrect="off"
            {...register(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_quantity` as `product_categories_distributions.${number}.products_distributions.${number}.total_value`)}
            forceValue
            value={watch(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_quantity`)}
            onChange={(event) => {
              register(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_quantity` as `product_categories_distributions.${number}.products_distributions.${number}.total_value`).onChange(event);
              onTotalValueChange();
            }}
            errorMessage={(errors?.product_categories_distributions?.[productCategoryIndex]?.products_distributions?.[productIndex] as { total_quantity?: { message?: string } })?.total_quantity?.message || undefined}
          />
        ) : (
          <CurrencyTextField
            label="Valor limite"
            {...register(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_value`)}
            onChange={(event) => {
              register(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_value`).onChange(event);
              onTotalValueChange();
            }}
            errorMessage={(errors?.product_categories_distributions?.[productCategoryIndex]?.products_distributions?.[productIndex] as { total_value?: { message?: string } })?.total_value?.message || undefined}
            forceValue
            value={watch(`product_categories_distributions.${productCategoryIndex}.products_distributions.${productIndex}.total_value`)}
          />
        )}

        <div className='flex gap-3'>
          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade maxima: ' : 'Total máximo: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? formatDecimalUnit(availableForAllocation, productDistribution.product.unit_for_quantity)
                : formatCurrency(availableForAllocation)}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade disponível: ' : 'Total disponível: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? formatDecimalUnit(available, productDistribution.product.unit_for_quantity)
                : formatCurrency(available)}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade empenhada: ' : 'Total empenhado: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? (allocated ? formatDecimalUnit(allocated, productDistribution.product.unit_for_quantity) : '--')
                : (allocated ? formatCurrency(allocated) : '--')}
            </span>
          </p>

          <p className='italic text-xs text-white-400'>
            {isQuantity ? 'Quantidade distribuída em todas filiais: ' : 'Total distribuído em todas filiais: '}
            <span className='whitespace-nowrap'>
              {isQuantity
                ? (distributed ? formatDecimalUnit(distributed, productDistribution.product.unit_for_quantity) : '--')
                : (distributed ? formatCurrency(distributed) : '--')}
            </span>
          </p>
        </div>
      </div>
    </Box>
  );
};

export default ProductDistributionFormItem;
